import { modelData } from 'app/lib/modelData'
/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import {
  autoUpdate,
  flip,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { ArrowUpIcon } from '@heroicons/react/24/outline'
import { MoonStars } from '@phosphor-icons/react'
import { useSearchParams } from 'react-router-dom'
import EmojiPicker from 'emoji-picker-react'
import { Check, Emoji as EmojiIcon, MultiplePages } from 'iconoir-react'
import { entries, flatMap, isEmpty, sortBy } from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useMutation, useQuery } from 'urql'
import { AgentDisabledDivider } from 'app/components/contacts/inbox/AgentDisabledDivider'
import { AgentEngagedDivider } from 'app/components/contacts/inbox/AgentEngagedDivider'
import { AppointmentBookedDivider } from 'app/components/contacts/inbox/AppointmentBookedDivider'
import { DateDivider } from 'app/components/contacts/inbox/DateDivider'
import { EmailComposer } from 'app/components/contacts/inbox/EmailComposer'
import { SegmentedControl } from 'app/components/action-center/SegmentedControl'
import { DebugTraceCard, TurnMetaChips, turnHasToolActivity } from 'app/components/contacts/inbox/DebugTraceCard'
import { MessageCard } from 'app/components/contacts/inbox/MessageCard'
import { DEBUG_TRACES_QUERY, SEND_SMS_MUTATION } from 'app/components/contacts/inbox/graphql'
import { useSmsProvider } from 'app/components/contacts/inbox/useSmsProvider'
import { useThreadDividers } from 'app/components/contacts/inbox/useThreadDividers'
import { useDraftRequest } from 'app/components/contacts/inbox/useDraftRequest'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import type { DraftRequest } from 'app/components/inbox/scheduling-dock/types'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { AIBadgePill } from 'app/components/Icon/AIBadgeIcon'
import { ScheduledTray } from 'app/components/sms-scheduling/ScheduledTray'
import { SendLaterButton } from 'app/components/sms-scheduling/SendLaterButton'
import { SmsTemplatePicker } from 'app/components/sms-templates/TemplatePicker'
import { Button } from 'app/components/ui/button'
import { Textarea } from 'app/components/ui/textarea'
import { HELD_MESSAGES_QUERY } from 'app/graphql/scheduledMessages'
import { useInboxDebugMode } from 'app/hooks/useInboxDebugMode'
import { useSmsScheduling } from 'app/hooks/useSmsScheduling'
import { isHeldRow, isHeldRowToShow, toHeldMessages } from 'app/lib/heldMessages'
import { getMessageDelivery, isOutboundMessage } from 'app/lib/messageDelivery'
import { extractTokenKeys, substituteTokens } from 'app/lib/messageTemplates'
import { nextSendableWallClock, sendAtLabel } from 'app/lib/scheduledSend'
import { OPT_OUT_LINE, hasOptOutWording, optOutAppendMessageId } from 'app/lib/smsOptOut'
import { isQuietHours, practiceLocalTimeLabel } from 'app/lib/smsQuietHours'
import { smsSegments } from 'app/lib/smsSegments'
import { classifySendSmsFailure } from 'app/lib/smsSendErrors'
import { MARK_MESSAGE_READ_MUTATION } from 'app/graphql/messageMutations'
import { gqlMutation } from 'app/lib/graphqlClient'
import { notifyError } from 'app/services/notify.client'
import type { AiConversationTurn, SendMessageInput } from 'app/generated/graphql'
import type { SmsTemplate } from 'app/generated/graphql'
import type { ContactJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import type { HeldMessage } from 'app/types/held-message'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'

const TURN_OFF_AI_CONFIRM = {
  variant: `danger` as const,
  title: `Turn off AI Agent?`,
  description: `The AI Agent will stop replying on this thread. AI is trigger-based and can't be turned back on manually. It will only re-engage when a new trigger fires.`,
  submitLabel: `Turn off`,
  cancelLabel: `Cancel`,
}

// Pulls the AI Agent toggle history for a thread so the conversation view can
// render historical "Agent Disabled" dividers anchored to the actual timestamp
// of each toggle-off — persistent milestones, not current-state indicators.

// Compliance opt-out check. If a record exists, Message.beforeSave on the API
// blocks every outbound message to this phone — AI and manual alike. We only
// need to know if one exists, hence pagination: { limit: 0 } and total.

type Props = {
  conversationKey: string
  messages?: Record<string, MessageJson[]>
  contact?: ContactJson
  relatedContacts?: ContactJson[]
  resolvedPhone?: string
  /**
   * A draft handed in from outside, e.g. the inbox scheduling dock after a
   * successful booking. Appends to whatever is already in the composer.
   */
  draftRequest?: DraftRequest
  aiHandling?: boolean
  /**
   * When true, render the Mark Read / Mark Unread pill + optional AI toggle in a
   * row above the compose input. The main inbox surface (PageContent) leaves this
   * off because that surface owns its own header which already has the same
   * controls. The contact drawer's Inbox tab turns it on because there is no
   * header to put them in.
   */
  showInlineMarkRead?: boolean
  /**
   * When true, the inline header row above the compose renders an AI Agent on/off
   * toggle on the left. Gated separately from `showInlineMarkRead` so callers can
   * hide it when the workspace doesn't have an active workflow.
   */
  showInlineAgentToggle?: boolean
  /**
   * Show super users the AI agent's tool calls without the inbox's debug toggle. The
   * contact drawer turns it on because the toggle lives on the main inbox page (TRK-5934).
   */
  showAgentToolCalls?: boolean
  agentEnabled?: boolean
  onAgentToggle?: (val: boolean) => void | Promise<void>
  onMarkAllRead?: () => void | Promise<void>
  onMarkUnread?: () => void | Promise<void>
  onTakeOver?: () => void | Promise<void>
  onMessage: (message: MessageJson) => void
}

const NEAR_BOTTOM_THRESHOLD = 120
const ANIMATED_NEW_MESSAGE_LIMIT = 5

function useContactInbox({ conversationKey, messages, contact, resolvedPhone, draftRequest, onMessage }: Props) {
  const user = useUser()
  const isSuperUser = user?.type === `super`
  const textArea = useRef<HTMLTextAreaElement>(null)
  const [searchParams] = useSearchParams()
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  // Single source of truth for the composer draft (the Textarea is controlled).
  const [draftText, setDraftText] = useState<string>(``)
  useDraftRequest(draftRequest, setDraftText)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [sendState, sendSmsMutation] = useMutation(SEND_SMS_MUTATION)
  const isSending = sendState.fetching
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set())
  const prevMessageIdsRef = useRef<Set<string>>(new Set())
  const hasInitializedRef = useRef<boolean>(false)
  const isNearBottomRef = useRef<boolean>(true)
  const newMessageAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const messageIds = useMemo(
    () =>
      Object.values(messages ?? {}).flatMap((group) =>
        group.map((message) => message.id).filter((id): id is string => Boolean(id)),
      ),
    [messages],
  )
  const sendToPhone = resolvedPhone || contact?.cellPhone

  // The location this composer sends from, which owns the practice clock, the
  // opt-out records and the provider.
  const optOutLocationId = contact?.locationId ?? searchParams.get(`locationId`) ?? undefined

  // CallRail appends `Reply STOP to opt out.` to a conversation's first text
  // and never reports it back, so the provider decides whether the inbox can
  // claim one was appended. Twilio and unknown both mean "render nothing".
  const smsProvider = useSmsProvider(optOutLocationId)

  const [confirm] = useConfirmation()

  // Scheduling: the quiet-hours banner's door, and the picker beside Send.
  // `scheduleMode` swaps the composer's primary action from Send to Schedule;
  // `pendingSendAtLocal` is the wall-clock time chosen either via the banner
  // (seeded with the next opening) or the picker (a specific pick).
  const [scheduleMode, setScheduleMode] = useState(false)
  const [channel, setChannel] = useState<`sms` | `email`>(`sms`)
  const [pendingSendAtLocal, setPendingSendAtLocal] = useState<string | undefined>(undefined)
  // The practice clock, the compliance opt-out gate, and the scheduled-send
  // controls, assembled the same way every other composer assembles them. Only
  // this surface goes further with the clock: SMS quiet hours mean no
  // business-initiated texts 8:00 PM to 8:00 AM at the practice, but replying to
  // a patient-initiated conversation is not a solicitation, so `quietHours` is
  // read together with who spoke last (below) rather than on its own.
  const {
    scheduled,
    timezone: quietTimezone,
    quiet: quietHours,
    resolved: quietResolved,
    optedOut: hasOptOutRecord,
    agentHandling: agentOnThread,
  } = useSmsScheduling({ contactId: contact?.id, phone: sendToPhone, locationId: optOutLocationId })
  const optedOutOfSms = hasOptOutRecord || contact?.smsMarketingConsent === false
  // The next 8:00 AM on the practice clock, recomputed on render so a composer
  // left open across midnight offers today's opening rather than yesterday's.
  const nextOpening = quietTimezone ? nextSendableWallClock(quietTimezone) : undefined
  const openingLabel = nextOpening && quietTimezone ? sendAtLabel(nextOpening, quietTimezone) : ``

  // The tray's own source of queued messages: contact-scoped and status:pending
  // only, so a message dispatched or cancelled elsewhere drops off the next
  // poll without a special case here.
  const [{ data: heldData }, refetchHeld] = useQuery({
    query: HELD_MESSAGES_QUERY,
    variables: {
      // `messages` returns held rows like any other, by decision: the API owner
      // declined an exclude-pending argument, so every caller states the statuses
      // it wants. Naming `status:pending` here is that statement, not a
      // placeholder waiting on the API. The cancelled and failed rows this tray
      // also explains come from the thread's own `messages` prop below.
      search: `contactId:${contact?.id} AND status:pending AND sendDate:*`,
      pagination: { limit: 25 },
    },
    pause: !scheduled.configured || !contact?.id,
    requestPolicy: `cache-and-network`,
  })
  const heldMessages = useMemo(() => toHeldMessages(heldData?.messages?.hits), [heldData])

  // A cancelled or failed held row is a real Message already sitting in the
  // thread's own list (the `messages` prop), not something the pending-only
  // query above returns. Pull it out here so the tray can explain it, and drop
  // the same row from the ordinary bubble list below: it was never sent.
  //
  // Recognized by `isHeldRow`, on the status vocabulary, NOT on `sendDate`
  // presence. All three of the API's cancel paths clear `sendDate` to null, so a
  // sendDate test misses every cancelled held message and it renders here as an
  // ordinary blue sent bubble, which is the one thing this feature exists to
  // prevent.
  const threadHeldRows = useMemo(() => {
    return flatMap(messages ?? {}, (msgs) => msgs)
      .filter((m): m is MessageJson & { id: string } => Boolean(m.id) && isHeldRow(m))
      .map(
        (m): HeldMessage => ({
          id: m.id,
          text: m.text ?? ``,
          sendDate: m.sendDate ?? undefined,
          status: m.status ?? undefined,
          errorCode: m.errorCode ?? undefined,
          contactId: m.contactId ?? undefined,
          to: m.to ?? ``,
          from: m.from ?? ``,
          sentByUserId: m.sentByUserId ?? undefined,
          createdDate: m.createdDate ?? ``,
          // Carry the read state: `ScheduledTray` hides a dismissed not-sent
          // notice on `read === true`, which is what keeps it gone across a
          // reload and for every other user (TRK-5945). Without it, a notice
          // dismissed by one load comes back on the next.
          read: m.read ?? undefined,
        }),
      )
  }, [messages])

  // Every held row on this thread, whether or not the tray has anything to say
  // about it. This drives the timeline exclusion below, so it must stay the
  // wider of the two lists: a row the user cancelled themselves is not worth a
  // tray band, but it still never went out and must not come back as a bubble.
  //
  // De-duplicated by id because the pending query and the thread's own list can
  // both carry the same row (once the API stops surfacing pending rows in
  // `messages` by default, this collapses to a plain concat).
  const heldRows = useMemo(() => {
    const byId = new Map<string, HeldMessage>()
    for (const m of [...heldMessages, ...threadHeldRows]) byId.set(m.id, m)
    return [...byId.values()]
  }, [heldMessages, threadHeldRows])

  // What the tray actually renders. `isHeldRowToShow` drops a row the user
  // cancelled themselves: it carries no errorCode, so the tray could only say
  // "This message was not sent." about an action the user just took.
  const trayMessages = useMemo(() => heldRows.filter((m) => isHeldRowToShow(m)), [heldRows])

  const heldIds = useMemo(() => new Set(heldRows.map((m) => m.id)), [heldRows])

  // A held or recently-cancelled row must never render as an ordinary bubble:
  // it has not been sent. Excluded here, from the copy of `messages` this hook
  // hands back to the component, so the timeline and the tray never show the
  // same message at once.
  const filteredMessages = useMemo(() => {
    if (!messages) return messages
    const result: Record<string, MessageJson[]> = {}
    for (const [date, msgs] of Object.entries(messages)) {
      const kept = msgs.filter((m) => !(m.id && heldIds.has(m.id)))
      if (kept.length) result[date] = kept
    }
    return result
  }, [messages, heldIds])

  // What this thread has actually SAID, newest last. Read off the held-excluded
  // list, never the raw `messages` prop: a queued outbound row is newer than
  // every real message by construction, so computing this from the prop would
  // make a message nobody has received the thread's latest. `patientSpokeLast`
  // would flip to false and the quiet-hours wall would replace the composer the
  // instant staff queued a reply, which is the exact opposite of the after-hours
  // case this feature exists for: patient texts in at 9pm, staff queue a reply
  // for 8am, thread loses its composer.
  const latestMessage = useMemo(() => {
    const sorted = sortBy(
      flatMap(filteredMessages ?? {}, (msgs) => msgs),
      `createdDate`,
    )
    return sorted[sorted.length - 1]
  }, [filteredMessages])
  const patientSpokeLast = !!latestMessage && !isOutboundMessage(latestMessage)
  // Never paint the block off the viewer's clock while the practice timezone
  // is still loading; the composer shows normally until the query settles.
  const quietHoursBlocked = quietResolved && quietHours && !patientSpokeLast

  // One scheduled message per thread. The tray's own picker is the way to
  // change it, so the composer stops offering a second. Send stays available.
  //
  // Invariant this depends on: ScheduledTray always sorts a pending row ahead
  // of a not-sent one, so whenever any row here is pending, the row the tray
  // actually shows is that pending one and offers its own picker for it. If
  // that ordering ever changed, this withholding and the tray's picker could
  // both go missing on the same render, leaving a queued message with no
  // "Schedule this message" control anywhere.
  const hasQueued = trayMessages.some((m) => m.status === `pending`)

  const cancelHeld = useCallback(
    async (id: string) => {
      if (await scheduled.cancel(id)) refetchHeld({ requestPolicy: `network-only` })
    },
    [scheduled, refetchHeld],
  )

  // Marks a not-sent notice seen so it stays gone, here and for everyone else
  // (TRK-5974). Returns whether the write landed: the tray keeps the row on
  // screen when it did not, rather than hiding something the next load brings
  // back.
  const dismissNotice = useCallback(
    async (id: string) => {
      const result = await gqlMutation(MARK_MESSAGE_READ_MUTATION, { id, read: true })
      if (result.error) {
        logError(`dismissNotice failed`, result.error)
        notifyError(`Couldn't dismiss this notice.`)
        return false
      }
      refetchHeld({ requestPolicy: `network-only` })
      return true
    },
    [refetchHeld],
  )

  // The one compliance gate on business-initiated SMS, shared by every immediate
  // send in this component: the composer's own Send and the tray's "Send anyway".
  // Both put a real text on a real phone right now, so both answer to it. There
  // is deliberately no second copy of this rule: the tray's button was added
  // without one, which made it a way to text someone at 10pm with no block and
  // no confirmation.
  //
  // Re-checks the clock here (not just the hook's minute tick) so the 8:00 PM /
  // 8:00 AM boundary is exact, and so Ctrl+Enter is covered. Returns false when
  // the send must not proceed.
  const passesQuietHoursGate = async (): Promise<boolean> => {
    if (!isQuietHours(quietTimezone)) return true
    if (!patientSpokeLast) {
      notifyError(`Quiet hours: texting is paused until 8:00 AM at this practice`)
      return false
    }
    try {
      await confirm({
        variant: `info`,
        title: `Send during quiet hours?`,
        description: `It's ${practiceLocalTimeLabel(quietTimezone)} at this practice. Quiet hours run from 8:00 PM to 8:00 AM. This patient messaged you last, so replying is allowed.`,
        submitLabel: `Send now`,
        cancelLabel: `Cancel`,
      })
      // oxlint-disable-next-line local/require-catch-logging
    } catch {
      return false
    }
    return true
  }

  // There is no "send now" mutation for a held row: sending anyway is a fresh
  // send through sendMessage's own path and gates, and the held row (cancelled
  // or failed) stays put as the record of why the original attempt did not go
  // out. `canSendAnyway` is derived from the row's STORED errorCode, so a row
  // cancelled for CONTACT_REPLIED still offers the button after that same
  // contact has gone on to reply STOP; the opt-out check here reads current
  // state, and the tray hides the button on it too, so the violation cannot be
  // reached by click or by keyboard.
  //
  // A plain function rather than a useCallback: it reads `optedOutOfSms` and
  // the quiet-hours gate, and a memo with unstated dependencies is a stale
  // closure waiting to happen on exactly the checks that must never be stale.
  // `sendMessage` below is a plain function for the same reason.
  const sendHeldNow = async (message: HeldMessage): Promise<boolean> => {
    const locationId = contact?.locationId ?? searchParams.get(`locationId`) ?? undefined
    if (!sendToPhone || !contact?.id || !locationId) {
      notifyError(`Can't send: no recipient selected for this thread`)
      return false
    }
    if (optedOutOfSms) {
      notifyError(`This number has opted out of SMS. Replies are blocked.`)
      return false
    }
    if (!(await passesQuietHoursGate())) return false
    const result = await sendSmsMutation({ input: { contactId: contact.id, text: message.text, locationId } })
    // Operation types carry GraphQL null where the cached model wants undefined.
    const sent = modelData(result.data?.sendSms)
    if (result.error || !sent?.id) {
      const reason = result.error?.message ?? `Empty response from server`
      logError(`sendHeldNow failed`, reason)
      notifyError(`Couldn't send message: ${classifySendSmsFailure(reason).reason}`)
      return false
    }
    onMessage(sent)
    refetchHeld({ requestPolicy: `network-only` })
    // The tray dismisses the row client-side once it knows this succeeded;
    // there is no mutation to mark the old cancelled/failed row resolved.
    return true
  }

  const rescheduleHeld = useCallback(
    async (id: string, text: string, sendAtLocal: string) => {
      if (!(await scheduled.reschedule(id, text, sendAtLocal))) {
        notifyError(`Couldn't move this message to that time`)
        return
      }
      refetchHeld({ requestPolicy: `network-only` })
    },
    [scheduled, refetchHeld],
  )

  function keyDown(evt: React.KeyboardEvent<HTMLTextAreaElement>) {
    debug(`inbox`, `keyDown`, evt.key, evt.ctrlKey, evt.metaKey)
    if (evt.key === `Enter` && (evt.ctrlKey || evt.metaKey)) {
      if (scheduleMode) {
        bg(() => submitSchedule())
        return
      }
      bg(() => sendMessage(evt as any))
    }
  }

  const sendMessage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const text = draftText
    debug(`inbox`, `sendMessage`, text)
    if (!text?.trim() || !sendToPhone || isSending) return
    const locationId = contact?.locationId ?? searchParams.get(`locationId`) ?? undefined
    if (!locationId) {
      notifyError(`Can't send: no location selected for this thread`)
      return
    }
    if (!contact?.id) {
      notifyError(`Can't send: no contact selected for this thread`)
      return
    }
    // The same gate the tray's "Send anyway" goes through; see
    // passesQuietHoursGate.
    if (!(await passesQuietHoursGate())) return
    const input: Pick<SendMessageInput, `contactId` | `text` | `locationId`> = {
      contactId: contact.id,
      text,
      locationId,
    }
    const result = await sendSmsMutation({ input })
    const message = modelData(result.data?.sendSms)
    if (result.error || !message?.id) {
      const reason = result.error?.message ?? `Empty response from server`
      const code = result.error?.graphQLErrors[0]?.extensions.code
      logError(`sendMessage failed`, reason)
      notifyError(
        `Couldn't send message: ${classifySendSmsFailure(reason, typeof code === `string` ? code : undefined).reason}`,
      )
      return
    }
    setDraftText(``)
    onMessage(message)
  }

  // Submits the composer's draft as a scheduled send instead of an immediate
  // one. Lives beside sendMessage: same draft, same contact/location
  // resolution owned by useScheduledSend, different verb.
  const submitSchedule = async () => {
    const text = draftText?.trim()
    if (!text || !pendingSendAtLocal) return
    const result = await scheduled.schedule(text, pendingSendAtLocal)
    if (!result.ok) {
      notifyError(result.reason ?? `Couldn't schedule this message`)
      return
    }
    setDraftText(``)
    setScheduleMode(false)
    setPendingSendAtLocal(undefined)
    // The one write that creates a held row, so the one the tray cannot learn
    // about any other way: the thread query excludes pending messages by
    // design, so a new queued message reaches the tray only through this read.
    // Without it the band keeps showing whatever it showed before, which on a
    // thread with an older not-sent row means the user schedules a message and
    // the tray answers with an unrelated amber notice.
    refetchHeld({ requestPolicy: `network-only` })
  }
  // TextArea grows with content up to a 350px cap, then scrolls internally. After
  // every adjustment we pin the scroll position to the bottom so the cursor / latest
  // character is always in view (this also covers programmatic inserts like emoji).
  const adjustHeight = () => {
    if (!textArea.current) return
    if (draftText.length > 0) {
      textArea.current.style.height = `auto`
      textArea.current.style.height = `${textArea.current.scrollHeight <= 350 ? textArea.current.scrollHeight : 350}px`
      textArea.current.scrollTop = textArea.current.scrollHeight
    } else textArea.current.style.height = `` // Resetting height when there is no text
  }

  const updateNearBottomState = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    isNearBottomRef.current = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD
  }, [])

  const scrollToBottom = useCallback((behavior: ScrollBehavior = `auto`) => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior,
      })
      isNearBottomRef.current = true
    }
  }, [])

  const floatingData = useFloating({
    open: isOpen,
    placement: `bottom-end`,
    elements: { reference: referenceElement, floating: popperElement },
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      flip({
        fallbackStrategy: `bestFit`,
        padding: 5,
      }),
    ],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context),
  ])

  useEffect(() => {
    hasInitializedRef.current = false
    prevMessageIdsRef.current = new Set()
    isNearBottomRef.current = true
    setNewMessageIds(new Set())
    if (newMessageAnimationTimeoutRef.current) {
      clearTimeout(newMessageAnimationTimeoutRef.current)
      newMessageAnimationTimeoutRef.current = undefined
    }
    // A schedule-in-progress belongs to the conversation it was started in.
    // conversationKey is a prop, not a React key, so switching threads does not
    // remount this component; without this, schedule mode (and the time it
    // carries) would silently follow the viewer into an unrelated thread.
    setScheduleMode(false)
    setPendingSendAtLocal(undefined)
    setChannel(`sms`)
  }, [conversationKey])

  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    updateNearBottomState()
    container.addEventListener(`scroll`, updateNearBottomState)

    const observer = new ResizeObserver(() => {
      updateNearBottomState()
    })
    observer.observe(container)

    return () => {
      container.removeEventListener(`scroll`, updateNearBottomState)
      observer.disconnect()
    }
  }, [updateNearBottomState])

  useLayoutEffect(() => {
    if (isEmpty(messages)) return

    const previousIds = prevMessageIdsRef.current
    const currentIds = new Set(messageIds)
    const appendedIds = messageIds.filter((id) => !previousIds.has(id))
    const wasNearBottom = isNearBottomRef.current

    prevMessageIdsRef.current = currentIds

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
      requestAnimationFrame(() => scrollToBottom())
      return
    }

    if (appendedIds.length === 0) {
      return
    }

    if (appendedIds.length <= ANIMATED_NEW_MESSAGE_LIMIT) {
      const incomingIds = new Set(appendedIds)
      setNewMessageIds(incomingIds)
      if (newMessageAnimationTimeoutRef.current) {
        clearTimeout(newMessageAnimationTimeoutRef.current)
      }
      newMessageAnimationTimeoutRef.current = setTimeout(() => {
        setNewMessageIds(new Set())
        newMessageAnimationTimeoutRef.current = undefined
      }, 400)
    }

    if (wasNearBottom) {
      requestAnimationFrame(() => scrollToBottom(`smooth`))
    }
  }, [messageIds, messages, scrollToBottom])

  useEffect(() => {
    adjustHeight()
  }, [draftText])

  useEffect(() => {
    return () => {
      if (newMessageAnimationTimeoutRef.current) {
        clearTimeout(newMessageAnimationTimeoutRef.current)
      }
    }
  }, [])

  return {
    isSuperUser,
    textArea,
    messagesContainerRef,
    draftText,
    isOpen,
    newMessageIds,
    floatingData,
    setReferenceElement,
    setPopperElement,
    setIsOpen,
    setDraftText,
    getReferenceProps,
    getFloatingProps,
    keyDown,
    sendMessage,
    sendToPhone,
    isSending,
    optedOutOfSms,
    agentOnThread,
    quietHoursBlocked,
    smsProvider,
    channel,
    setChannel,
    scheduleMode,
    setScheduleMode,
    pendingSendAtLocal,
    setPendingSendAtLocal,
    scheduled,
    quietTimezone,
    nextOpening,
    openingLabel,
    submitSchedule,
    // Overrides the raw `messages` prop: held/cancelled rows are pulled out so
    // the timeline never shows a message twice, once as a bubble and once in
    // the tray.
    messages: filteredMessages,
    trayMessages,
    hasQueued,
    cancelHeld,
    dismissNotice,
    sendHeldNow,
    rescheduleHeld,
  }
}

type ContactInboxComponentProps = Omit<Props, `conversationKey`> & ReturnType<typeof useContactInbox>

const ContactInboxComponent = observer(function ContactInboxComponent({
  messages,
  contact,
  relatedContacts,
  aiHandling,
  showInlineMarkRead,
  showInlineAgentToggle,
  showAgentToolCalls,
  agentEnabled,
  onAgentToggle,
  onMarkAllRead,
  onMarkUnread,
  onTakeOver,
  isSuperUser,
  textArea,
  messagesContainerRef,
  isOpen,
  draftText,
  newMessageIds,
  floatingData,
  setReferenceElement,
  setPopperElement,
  setIsOpen,
  setDraftText,
  getReferenceProps,
  getFloatingProps,
  keyDown,
  sendMessage,
  sendToPhone,
  isSending,
  optedOutOfSms,
  agentOnThread,
  quietHoursBlocked,
  smsProvider,
  scheduleMode,
  setScheduleMode,
  pendingSendAtLocal,
  setPendingSendAtLocal,
  scheduled,
  quietTimezone,
  nextOpening,
  openingLabel,
  submitSchedule,
  trayMessages,
  hasQueued,
  cancelHeld,
  dismissNotice,
  sendHeldNow,
  rescheduleHeld,
  onMessage,
  channel,
  setChannel,
}: ContactInboxComponentProps) {
  const [confirm] = useConfirmation()
  const [searchParams] = useSearchParams()
  const inboxEmailEnabled = useFeatureToggle(`inbox-email`)
  const channelSwitch = (
    <SegmentedControl
      options={[
        { id: `sms` as const, label: `Text` },
        { id: `email` as const, label: `Email` },
      ]}
      value={channel}
      onSelect={setChannel}
    />
  )

  const handleTakeOver = async () => {
    if (!onTakeOver) return
    try {
      await confirm(TURN_OFF_AI_CONFIRM)
      // oxlint-disable-next-line local/require-catch-logging
    } catch {
      return
    }
    await onTakeOver()
    refetchDividers()
  }

  const handleInlineTurnOff = async () => {
    if (!onAgentToggle) return
    try {
      await confirm(TURN_OFF_AI_CONFIRM)
      // oxlint-disable-next-line local/require-catch-logging
    } catch {
      return
    }
    await onAgentToggle(false)
    refetchDividers()
  }

  const sortedMessages = useMemo(
    () =>
      sortBy(
        flatMap(messages ?? {}, (msgs) => msgs),
        `createdDate`,
      ),
    [messages],
  )

  // Engagement dividers: one bar per workflow firing. Drives the inline
  // "Agent Engaged: <workflow>" divider above each initial agent message.
  // Uses the backend-authoritative `Message.isInitialMessage` flag (sourced
  // from `AIConversationTurn.isInitialMessage` on the matching turn).
  const engagementDividers = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of sortedMessages) {
      if (!m?.id || !m.isInitialMessage) continue
      map.set(m.id, m.aiWorkflowName ?? `AI Agent`)
    }
    return map
  }, [sortedMessages])

  const { dividersBeforeMessage, refetchDividers } = useThreadDividers(sortedMessages, contact?.patientIds)

  // iMessage-style receipt: label only the thread's latest outbound message,
  // and only when the carrier confirmed delivery. Stays through inbound replies
  // (like iMessage), disappears when a newer outbound replaces it. Providers
  // without delivery callbacks (CallRail caps at `sent`) get no label — we
  // never claim more certainty than the provider reports.
  const deliveredReceiptId = useMemo(() => {
    for (let i = sortedMessages.length - 1; i >= 0; i--) {
      const m = sortedMessages[i]!
      if (!m.id || m.channel === `email` || !isOutboundMessage(m)) continue
      return getMessageDelivery(m).status === `delivered` ? m.id : undefined
    }
    return undefined
  }, [sortedMessages])

  // At most one message per conversation carries CallRail's append. Same shape
  // as deliveredReceiptId above: resolve one id, compare per message.
  const optOutAppendId = useMemo(
    () => optOutAppendMessageId(sortedMessages, smsProvider),
    [sortedMessages, smsProvider],
  )

  // "First" means the first message WE send. CallRail appends to the first
  // outbound text of a conversation regardless of whether the patient wrote
  // in first, so an inbound message does not disqualify the next reply.
  const hasSentOutbound = useMemo(
    () => sortedMessages.some((m) => m.channel !== `email` && isOutboundMessage(m)),
    [sortedMessages],
  )

  const inlineHasUnread = useMemo(
    () => sortedMessages.some((m) => !m.read && !m.sentByUserId && !m.isAgent),
    [sortedMessages],
  )
  const inlineIncomingCount = useMemo(
    () => sortedMessages.filter((m) => !m.sentByUserId && !m.isAgent).length,
    [sortedMessages],
  )
  const lastIncomingDate = useMemo(
    () => [...sortedMessages].reverse().find((m) => !isOutboundMessage(m))?.createdDate,
    [sortedMessages],
  )

  // Super-user debug mode: fetch the AI agent's conversation turns for this
  // thread and render each turn's tool calls/results inline. Paused unless a
  // super user has debug mode on (or the caller shows tool calls outright), so
  // regular users never issue the query.
  const { debugMode } = useInboxDebugMode()
  const debugThreadId = useMemo(() => sortedMessages.find((m) => m.threadId)?.threadId, [sortedMessages])
  const debugEnabled = isSuperUser && (debugMode || !!showAgentToolCalls) && !!debugThreadId
  const [tracesQuery] = useQuery<{ aiConversationTurns: { hits: AiConversationTurn[] } }>({
    query: DEBUG_TRACES_QUERY,
    variables: {
      threadId: debugThreadId,
      sort: { field: `createdDate`, direction: `ASC` },
      pagination: { limit: 500 },
    },
    pause: !debugEnabled,
    requestPolicy: `cache-and-network`,
  })
  const debugTurns = useMemo<AiConversationTurn[]>(
    () => (debugEnabled ? (tracesQuery.data?.aiConversationTurns?.hits ?? []) : []),
    [debugEnabled, tracesQuery.data],
  )
  // Turns link to the message they produced via `messageId`. Render each trace
  // next to that message; turns with no linked message (agent didn't send, or
  // errored before sending) fall through to an "unlinked" section at the tail.
  const debugTracesByMessageId = useMemo(() => {
    const map = new Map<string, AiConversationTurn[]>()
    for (const turn of debugTurns) {
      if (!turn.messageId) continue
      const list = map.get(turn.messageId) ?? []
      list.push(turn)
      map.set(turn.messageId, list)
    }
    return map
  }, [debugTurns])
  const debugUnlinkedTurns = useMemo(() => {
    const renderedIds = new Set(sortedMessages.map((m) => m.id).filter((id): id is string => Boolean(id)))
    return debugTurns.filter((turn) => !turn.messageId || !renderedIds.has(turn.messageId))
  }, [debugTurns, sortedMessages])

  // CallRail's append lands on the wire, so it bills. Counting only what the
  // user typed under-reports the cost of a first message that lands near a
  // segment boundary.
  const composerAppendsOptOut =
    !hasSentOutbound && smsProvider === `callrail` && draftText.length > 0 && !hasOptOutWording(draftText)
  const composerSegments = smsSegments(composerAppendsOptOut ? `${draftText}\n${OPT_OUT_LINE}` : draftText).segments

  const insertTemplate = (template: SmsTemplate) => {
    // Resolve against the open contact now; anything unresolvable stays
    // literal {{…}} so the amber hint below can flag it for review.
    const { text } = substituteTokens(template.body, { contact }, { keepUnresolved: true })
    setDraftText((prev) => {
      if (!prev) return text
      return /\s$/.test(prev) ? `${prev}${text}` : `${prev} ${text}`
    })
  }
  // Live-derived (not insert-time state): also catches tokens the user types
  // or edits by hand.
  const draftTokens = useMemo(() => extractTokenKeys(draftText), [draftText])

  return (
    <>
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto bg-surface px-4 pb-6 md:px-10">
        {entries<MessageJson[]>(messages).map(([date, msgs], i) => {
          return (
            <div key={date} className="flex flex-col">
              <DateDivider text={date} className={i === 0 ? `mt-4 hidden` : `my-4`} />
              <div className={`flex flex-col gap-y-4 ${i === 0 ? `pt-4` : ``}`}>
                {msgs?.map((m, j) => {
                  const eventDividers = m.id ? dividersBeforeMessage.beforeId.get(m.id) : undefined
                  const messageTurns = debugEnabled && m.id ? debugTracesByMessageId.get(m.id) : undefined
                  // Every turn's meta renders inline by the timestamp; tool turns
                  // additionally render a right-aligned bubble with their results
                  // (with meta hidden there since it already shows inline).
                  const toolTurns = messageTurns?.filter(turnHasToolActivity)
                  const metaSlot = messageTurns?.map((turn) => <TurnMetaChips key={`meta-${turn.id}`} turn={turn} />)
                  return (
                    <div
                      key={m.id ?? `${date}-${j}`}
                      className={m.id && newMessageIds.has(m.id) ? `animate-message-slide-up` : ``}
                    >
                      {/* useThreadDividers emits agent-disabled + appointment items,
                          anchored together and rendered here by kind. */}
                      {eventDividers?.map((e) =>
                        e.kind === `appointment` ? (
                          <AppointmentBookedDivider
                            key={`appt-${e.id}`}
                            date={e.date}
                            time={e.time}
                            type={e.type}
                            className="my-4"
                          />
                        ) : (
                          <AgentDisabledDivider key={`disabled-${e.id}`} className="my-4" />
                        ),
                      )}
                      {m.id != null && engagementDividers.has(m.id) && (
                        <AgentEngagedDivider
                          workflowName={engagementDividers.get(m.id) ?? `AI Agent`}
                          className="my-4"
                        />
                      )}
                      {toolTurns && toolTurns.length > 0 ? (
                        // Message + its tool bubbles share a width-capped column so
                        // the debug bubble matches the agent message width.
                        <div className="ml-auto flex w-full max-w-[75%] flex-col gap-1">
                          <div data-testid="message-card">
                            <MessageCard
                              message={m}
                              contact={contact}
                              isSuperUser={isSuperUser}
                              metaSlot={metaSlot}
                              showDelivered={m.id != null && m.id === deliveredReceiptId}
                              showOptOutAppend={m.id != null && m.id === optOutAppendId}
                              fullWidth
                            />
                          </div>
                          {toolTurns.map((turn) => (
                            <DebugTraceCard key={`trace-${turn.id}`} turn={turn} hideMeta fullWidth />
                          ))}
                        </div>
                      ) : (
                        <div data-testid="message-card">
                          <MessageCard
                            message={m}
                            contact={contact}
                            isSuperUser={isSuperUser}
                            metaSlot={metaSlot}
                            showDelivered={m.id != null && m.id === deliveredReceiptId}
                            showOptOutAppend={m.id != null && m.id === optOutAppendId}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
        {/* Divider events (agent-disabled or appointment-booked) with timestamps
            AFTER every message render at the tail. */}
        {dividersBeforeMessage.tail.map((e) =>
          e.kind === `appointment` ? (
            <AppointmentBookedDivider
              key={`appt-tail-${e.id}`}
              date={e.date}
              time={e.time}
              type={e.type}
              className="my-4"
            />
          ) : (
            <AgentDisabledDivider key={`disabled-tail-${e.id}`} className="my-4" />
          ),
        )}
        {/* Debug turns not linked to any rendered message (agent didn't send a
            message, or errored before sending). Ordered by createdDate ASC. */}
        {debugEnabled && debugUnlinkedTurns.length > 0 && (
          <div className="mt-6 flex flex-col">
            <div className="text-label-xs font-semibold uppercase tracking-wide text-amber-700">
              Unlinked agent turns
            </div>
            {debugUnlinkedTurns.map((turn) => (
              <DebugTraceCard key={`trace-unlinked-${turn.id}`} turn={turn} />
            ))}
          </div>
        )}
      </div>
      {quietTimezone && (
        <ScheduledTray
          held={trayMessages}
          timezone={quietTimezone}
          // The tray still renders for an opted-out contact: a queued message
          // to that number needs cancelling, and hiding the row would hide the
          // only control that does it. What the opt-out removes is "Send
          // anyway", which reads its verdict from the row's stored errorCode and
          // would otherwise offer an immediate text to a number that has since
          // replied STOP.
          optedOut={optedOutOfSms}
          // Withholds reschedule and "Send anyway" while the agent is on the
          // thread, without hiding the row: a message queued before the agent
          // took over still needs its Cancel.
          agentHandling={agentOnThread}
          // Same `scheduled` instance the composer's own SendLaterButton
          // gates on below: this thread has one useScheduledSend, so cancel
          // and reschedule from the tray share the same in-flight flag as the
          // composer's own schedule call.
          scheduling={scheduled.scheduling}
          onCancel={(id) => bg(() => cancelHeld(id))}
          onDismiss={dismissNotice}
          // Not fire-and-forget: the tray awaits this to know whether to
          // dismiss the row, since there is no mutation that would do it.
          onSendAnyway={sendHeldNow}
          onReschedule={(id, text, sendAtLocal) => bg(() => rescheduleHeld(id, text, sendAtLocal))}
        />
      )}
      <form onSubmit={sendMessage} data-testid="composer-form" className="shrink-0 bg-surface px-4 py-4 md:px-6">
        {showInlineMarkRead && !aiHandling && (
          <InlineMarkReadRow
            lastMessageDate={lastIncomingDate}
            hasUnread={inlineHasUnread}
            incomingCount={inlineIncomingCount}
            showAgentToggle={showInlineAgentToggle}
            agentEnabled={agentEnabled}
            onTurnOffAgent={handleInlineTurnOff}
            onMarkAllRead={onMarkAllRead}
            onMarkUnread={onMarkUnread}
          />
        )}
        {inboxEmailEnabled && !aiHandling && <div className="mb-2 flex">{channelSwitch}</div>}
        {inboxEmailEnabled && (
          <div hidden={channel !== `email` || aiHandling}>
            <EmailComposer
              key={contact?.id}
              contact={contact}
              locationId={contact?.locationId ?? searchParams.get(`locationId`) ?? undefined}
              onMessage={onMessage}
            />
          </div>
        )}
        {inboxEmailEnabled && channel === `email` && !aiHandling ? null : optedOutOfSms ? (
          // Compliance opt-out wins over every other compose state. Backend's
          // Message.beforeSave drops any outbound (AI or manual) to this phone,
          // so we replace the composer entirely rather than show a disabled
          // input the user might keep typing into.
          <div
            aria-label="SMS opted out. This number has opted out of receiving SMS"
            className="flex items-center gap-2 rounded-md border-2 border-default bg-surface-muted/60 py-2.5 pl-3 pr-2"
          >
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-strong text-subtle"
            >
              ⛔
            </span>
            <span className="flex-1 text-body-sm font-medium leading-[20px] tracking-wide text-default">
              This number has opted out of SMS. Replies are blocked.
            </span>
          </div>
        ) : aiHandling ? (
          // Single row when AI is on: the input area itself becomes the AI banner.
          // No stacked disabled input below — that read as two stacked "AI Agent
          // is..." messages saying the same thing.
          <div
            aria-label="AI Agent is replying on this thread"
            className="flex items-center gap-2 rounded-md border-2 border-default bg-surface-muted/60 py-2.5 pl-3 pr-2"
          >
            <AIBadgePill className="shrink-0" />
            <span className="flex-1 truncate text-body-sm font-medium leading-[20px] tracking-wide text-subtle">
              AI Agent is replying on this thread
            </span>
            <Button type="button" variant="white" size="md" shape="pill" onClick={handleTakeOver}>
              Take over
            </Button>
          </div>
        ) : quietHoursBlocked && !scheduleMode ? (
          // Quiet hours (compliance): business-initiated texting is paused
          // 8:00 PM to 8:00 AM on the practice clock. Same treatment as the
          // opt-out state above: replace the composer rather than disable it.
          // What changes with scheduling on is that the replacement is a door
          // instead of a wall: the message can be written now and queued for
          // the next opening, so after-hours work does not have to stop.
          // Replies to a patient-initiated thread never reach this branch;
          // they keep the composer and confirm at send time instead.
          <div
            aria-label="Quiet hours. Texting is paused from 8:00 PM to 8:00 AM at this practice"
            className="flex items-center gap-2 rounded-md border-2 border-default bg-surface-muted/60 py-2.5 pl-3 pr-2"
          >
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-strong text-subtle"
            >
              <MoonStars className="h-4 w-4" weight="fill" />
            </span>
            <span className="flex-1 text-body-sm font-medium leading-[20px] tracking-wide text-default">
              {scheduled.available
                ? `Quiet hours · Texting is paused until 8:00 AM. You can write it now and schedule it.`
                : `Quiet hours · Texting is paused from 8:00 PM to 8:00 AM at this practice.`}
            </span>
            {scheduled.available && (
              <Button
                type="button"
                variant="white"
                size="md"
                className="rounded-full"
                onClick={() => {
                  setPendingSendAtLocal(nextOpening)
                  setScheduleMode(true)
                }}
              >
                Schedule for {openingLabel}
              </Button>
            )}
          </div>
        ) : (
          <>
            {!hasSentOutbound && sendToPhone && smsProvider === `callrail` && (
              <p className="px-1 pb-1 text-xs text-minimal">
                First text to this contact. CallRail adds “{OPT_OUT_LINE}” automatically when the message has no opt-out
                wording.
              </p>
            )}
            {/* Below md the three icon buttons plus the segment counter left the
                message field around 100px wide, so the field takes its own
                full-width line and the controls wrap under it. Every override
                is max-md: so the desktop row is untouched. */}
            <div className="flex items-end gap-2 rounded-md border-2 border-primary-200 bg-surface py-2.5 pl-2 pr-2 shadow-[0_1px_2px_color-mix(in_oklab,var(--fg-strong)_4%,transparent)] transition-all focus-within:border-primary-400 focus-within:shadow-[0_0_0_4px_var(--primary-100)] max-md:flex-wrap">
              <SmsTemplatePicker
                trigger={
                  <Button
                    type="button"
                    variant="icon"
                    size="icon"
                    title="Use a template"
                    aria-label="Use a template"
                    disabled={!sendToPhone}
                    className="h-11 w-11 rounded-md bg-surface-muted text-default hover:bg-surface-strong hover:text-strong"
                  >
                    <MultiplePages className="h-6 w-6" strokeWidth={2} />
                  </Button>
                }
                currentBody={draftText}
                renderTemplate={(t) => substituteTokens(t.body, { contact }, { keepUnresolved: true }).text}
                onSelect={insertTemplate}
              />
              <Button
                ref={setReferenceElement}
                {...getReferenceProps()}
                type="button"
                variant="icon"
                size="icon"
                title="Insert emoji"
                aria-label="Insert emoji"
                className="h-11 w-11 rounded-md bg-surface-muted text-default hover:bg-surface-strong hover:text-strong"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                <EmojiIcon className="h-6 w-6" strokeWidth={2} />
              </Button>
              <SendLaterButton
                timezone={quietTimezone}
                disabled={!sendToPhone}
                // One scheduled message per thread: while the tray has a
                // pending row, the composer withholds its own clock. The
                // tray's own picker is the way to change the queued one.
                unavailableReason={
                  hasQueued ? `A message is already scheduled on this conversation` : scheduled.unavailableReason
                }
                onSchedule={(sendAtLocal) => {
                  setPendingSendAtLocal(sendAtLocal)
                  setScheduleMode(true)
                }}
              />
              <Textarea
                ref={textArea}
                rows={1}
                disabled={!sendToPhone}
                placeholder={!sendToPhone ? `No phone number for this contact` : `Message`}
                style={{ scrollbarWidth: `none`, msOverflowStyle: `none` }}
                className="min-h-[44px] min-w-0 flex-1 resize-none overflow-y-auto border-0 bg-transparent px-1 py-2.5 text-body-md leading-[22px] text-strong placeholder:text-minimal shadow-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:text-minimal max-md:order-first max-md:w-full max-md:basis-full [&::-webkit-scrollbar]:hidden"
                onKeyDown={keyDown}
                value={draftText}
                onChange={(evt) => setDraftText(evt.target.value)}
              />
              {draftText.length > 0 && (
                <span className="mb-2.5 shrink-0 whitespace-nowrap text-xs text-minimal">
                  {composerSegments} {composerSegments === 1 ? `segment` : `segments`}
                </span>
              )}
              {scheduleMode && pendingSendAtLocal ? (
                // In schedule mode the primary action queues the draft for
                // the chosen time instead of sending it now; it is a regular
                // button, not a submit, so it never races the form's onSubmit
                // (still wired to sendMessage for the normal path). Cancel is
                // the only way back to immediate Send once here, so it just
                // clears the local schedule state and lets the existing
                // branching (quiet hours or not) decide what the composer
                // looks like next, rather than special-casing the return.
                <>
                  <Button
                    type="button"
                    variant="white"
                    size="md"
                    onClick={() => {
                      setScheduleMode(false)
                      setPendingSendAtLocal(undefined)
                    }}
                    className="shrink-0"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    disabled={!draftText.trim() || scheduled.scheduling}
                    onClick={submitSchedule}
                    className="shrink-0 max-md:ml-auto"
                  >
                    {scheduled.scheduling
                      ? `Scheduling…`
                      : `Schedule for ${sendAtLabel(pendingSendAtLocal, quietTimezone!)}`}
                  </Button>
                </>
              ) : (
                <Button
                  type="submit"
                  variant="primary"
                  size="icon"
                  disabled={!sendToPhone || isSending || draftText.length === 0}
                  title="Send message"
                  aria-label="Send message"
                  className="h-11 w-11 max-md:ml-auto"
                >
                  <ArrowUpIcon className="h-5 w-5" strokeWidth={3} />
                </Button>
              )}
              {isOpen && (
                <FloatingPortal>
                  <div
                    id="emoji-picker"
                    className="z-50"
                    ref={setPopperElement}
                    style={floatingData.floatingStyles}
                    {...getFloatingProps()}
                  >
                    <EmojiPicker
                      className="emoji-picker"
                      previewConfig={{ showPreview: false }}
                      open={isOpen}
                      onEmojiClick={(emoji) => setDraftText((prev) => prev + emoji.emoji)}
                    />
                  </div>
                </FloatingPortal>
              )}
            </div>
            {draftTokens.length > 0 && (
              <p className="px-1 pt-1 text-xs text-amber-600">
                This message still has {`{{placeholders}}`} that couldn't be filled. They'll be sent as literal text
                unless you edit them.
              </p>
            )}
          </>
        )}
      </form>
    </>
  )
})

function ContactInbox(props: Props) {
  const hookState = useContactInbox(props)
  return <ContactInboxComponent {...props} {...hookState} />
}

/**
 * Mark Read / Mark Unread header row rendered inside the compose form, above the
 * input pill. Used by surfaces without their own header (the contact drawer's
 * Inbox tab) — the main inbox keeps these controls in InboxHeader instead.
 * Left side shows the latest-activity timestamp as quiet context so the row
 * doesn't feel half-empty when the action sits on the right. When the AI Agent
 * is enabled on this thread, the same row exposes an off-only "Turn off"
 * control — there is no manual "turn on" because AI is trigger-based.
 */
function InlineMarkReadRow({
  lastMessageDate,
  hasUnread,
  incomingCount,
  showAgentToggle,
  agentEnabled,
  onTurnOffAgent,
  onMarkAllRead,
  onMarkUnread,
}: {
  lastMessageDate?: string
  hasUnread: boolean
  incomingCount: number
  showAgentToggle?: boolean
  agentEnabled?: boolean
  onTurnOffAgent?: () => void | Promise<void>
  onMarkAllRead?: () => void | Promise<void>
  onMarkUnread?: () => void | Promise<void>
}) {
  const canMarkUnread = incomingCount > 0
  const lastActivity = lastMessageDate ? formatLastActivity(lastMessageDate) : undefined
  const showAgentOffButton = showAgentToggle && agentEnabled !== false && !!onTurnOffAgent
  return (
    <div className="mb-2 flex shrink-0 items-center justify-between gap-x-3">
      <div className="flex min-w-0 items-center gap-x-3">
        {showAgentOffButton && (
          <div className="flex shrink-0 items-center gap-x-1.5">
            <AIBadgePill className="shrink-0" />
            <Button type="button" variant="white" size="sm" shape="pill" onClick={() => onTurnOffAgent?.()}>
              Turn off
            </Button>
          </div>
        )}
        {lastActivity && <span className="truncate text-label-xs text-subtle">{`Last reply · ${lastActivity}`}</span>}
      </div>
      {hasUnread && onMarkAllRead ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onMarkAllRead()}
          className="rounded-full border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100 hover:text-primary-700"
        >
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
          Mark read
        </Button>
      ) : (
        onMarkUnread && (
          <Button
            type="button"
            variant="white"
            size="sm"
            shape="pill"
            disabled={!canMarkUnread}
            onClick={() => onMarkUnread()}
            title={canMarkUnread ? undefined : `Nothing to mark unread. No customer messages in this thread`}
          >
            Mark as unread
          </Button>
        )
      )}
    </div>
  )
}

function formatLastActivity(date: string): string {
  const m = moment.utc(date)
  const diffMinutes = moment().diff(m, `minutes`)
  if (diffMinutes < 1) return `just now`
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return m.local().format(`MMM D`)
}

export { ContactInbox, ContactInboxComponent }
export type { ContactInboxComponentProps }
